import { FC, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, InputBase, TableCell, TableRow, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { routes } from '@app/routes';
import { useDirection } from '@hooks';
import { useTrans } from '@libs/i18n';
import { CaretRightIcon, MagnifyingGlassIcon, PlusIcon } from '@phosphor-icons/react';

import { useAdminTenants } from '../api/admin-api';
import { formatDate } from '../components/adminFormat';
import { AdminStatusChip } from '../components/AdminStatusChip';
import { AdminTable } from '../components/AdminTable';
import { adminButtonSx, adminColors } from '../components/adminTheme';
import { ProvisionTenantModal } from '../components/ProvisionTenantModal';

const initials = (name: string) =>
  name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase();

export const AdminTenantsPage: FC = observer(() => {
  const theme = useTheme();
  const { isRtl } = useDirection();
  const c = adminColors(theme);
  const { t } = useTrans();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [provisionOpen, setProvisionOpen] = useState(false);

  const { data: tenants, isLoading } = useAdminTenants();

  const rows = useMemo(() => {
    const list = tenants ?? [];
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((o) =>
      `${o.displayName ?? ''} ${o.name} ${o.sector ?? ''}`.toLowerCase().includes(q),
    );
  }, [tenants, query]);

  const columns = [
    { key: 'org', label: t('app.admin.orgs.table.organization'), width: '26%' },
    { key: 'tenant', label: t('app.admin.orgs.table.tenantId'), width: '18%' },
    { key: 'sector', label: t('app.admin.orgs.table.sector'), width: '16%' },
    { key: 'assess', label: t('app.admin.orgs.table.assessments'), align: 'center' as const, width: '12%' },
    { key: 'status', label: t('app.admin.orgs.table.status'), width: '12%' },
    { key: 'created', label: t('app.admin.orgs.table.created'), width: '12%' },
    { key: 'caret', label: '', align: 'right' as const, width: '4%' },
  ];

  return (
    <Box sx={{ flex: 1, overflow: 'auto', px: 3.5, py: 2.75, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, mb: 2.25, flexWrap: 'wrap' }}>
        <Box>
          <Typography sx={{ fontSize: 23, fontWeight: 700, color: c.ink, mb: 0.375 }}>
            {t('app.admin.orgs.title')}
          </Typography>
          <Typography sx={{ fontSize: 13, color: c.sub }}>{t('app.admin.orgs.subtitle')}</Typography>
        </Box>
        <Button
          onClick={() => setProvisionOpen(true)}
          startIcon={<PlusIcon size={16} />}
          sx={{ ...adminButtonSx(theme).solid, px: 2, py: 1.125, borderRadius: 2.75 }}
        >
          {t('app.admin.orgs.provision')}
        </Button>
      </Box>

      {/* Search bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mb: 1.5,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.125,
            backgroundColor: theme.palette.grey[200],
            border: `1px solid ${c.border}`,
            borderRadius: 2.5,
            px: 1.5,
            py: 1,
            flex: 1,
            maxWidth: 420,
          }}
        >
          <MagnifyingGlassIcon size={16} color={c.faint} />
          <InputBase
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('app.admin.orgs.search')}
            sx={{ flex: 1, fontSize: 13, color: c.ink }}
          />
        </Box>
        <Typography sx={{ fontSize: 12, color: c.faint }}>
          {t('app.admin.orgs.count', { shown: rows.length, total: tenants?.length ?? 0 })}
        </Typography>
      </Box>

      <AdminTable
        columns={columns}
        minWidth={860}
        isLoading={isLoading}
        isEmpty={!rows.length}
        emptyLabel={t('app.admin.orgs.empty')}
      >
        {rows.map((o) => (
          <TableRow
            key={o.id}
            hover
            onClick={() => navigate(routes.adminTenantDetail(o.id))}
            sx={{ cursor: 'pointer' }}
          >
            <TableCell sx={{ borderBottom: `1px solid ${c.border}` }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.375 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 2.25,
                    backgroundColor: theme.palette.grey[200],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 600,
                    color: c.sub,
                    flexShrink: 0,
                  }}
                >
                  {initials(o.displayName || o.name)}
                </Box>
                <Typography sx={{ fontSize: 13, fontWeight: 500, color: c.ink }}>
                  {o.displayName || o.name}
                </Typography>
              </Box>
            </TableCell>
            <TableCell sx={{ borderBottom: `1px solid ${c.border}`, fontFamily: 'monospace', fontSize: 12.5, color: c.sub }}>
              {o.name}
            </TableCell>
            <TableCell sx={{ borderBottom: `1px solid ${c.border}`, fontSize: 12.5, color: c.sub }}>
              {o.sector || '—'}
            </TableCell>
            <TableCell align="center" sx={{ borderBottom: `1px solid ${c.border}`, fontSize: 13, fontWeight: 500, color: c.ink }}>
              {o.assessmentCount ?? 0}
            </TableCell>
            <TableCell sx={{ borderBottom: `1px solid ${c.border}` }}>
              <AdminStatusChip status={o.enabled === false ? 'suspended' : 'active'} />
            </TableCell>
            <TableCell sx={{ borderBottom: `1px solid ${c.border}`, fontSize: 12.5, color: c.sub }}>
              {formatDate(o.createdAt)}
            </TableCell>
            <TableCell align="right" sx={{ borderBottom: `1px solid ${c.border}` }}>
              <CaretRightIcon size={15} color={theme.palette.grey[500]} style={{ transform: isRtl ? 'scaleX(-1)' : 'none' }} />
            </TableCell>
          </TableRow>
        ))}
      </AdminTable>

      <ProvisionTenantModal open={provisionOpen} onClose={() => setProvisionOpen(false)} />
    </Box>
  );
});
